import { jobsClient } from "@/shared/api/clients";
import type { ReportJobStatus } from "../../../shared/interfaces/JobStatus";

/** Shape of GET /jobStatus — the server may send raw pipeline statuses
 *  (STARTED/GENERATING/EXTRACTING) that the registry normalizes to PENDING. */
export interface JobStatusSnapshot {
  jobId: string;
  jobStatus: ReportJobStatus | string;
  reportId?: string;
  progress?: number;
  message?: string;
  /** Present when jobStatus is AWAITING_TRANSCRIPT_DECISION: the videos that
   *  produced no usable transcript. Drives the continue/cancel prompt, so no
   *  extra fetch is needed to name them. */
  failedTranscriptionVideoIds?: string[];
}

export async function getJobStatus(
  jobId: string,
  signal?: AbortSignal,
): Promise<JobStatusSnapshot> {
  const response = await jobsClient.get("/jobStatus", {
    params: { jobId },
    signal,
  });
  return response.data as JobStatusSnapshot;
}

/** Re-submits an existing (failed) job under the same jobId. Also the
 *  "continue" call for a report parked in AWAITING_TRANSCRIPT_DECISION — the
 *  backend reads the decision implicitly from the parked state, so the body
 *  must carry nothing but the jobId. */
export async function retryJob(jobId: string): Promise<void> {
  await jobsClient.post("/jobs", { jobId });
}

/**
 * Expands a narrative-only report into the full schema-bound report. The job
 * is keyed by the report id (reportId === jobId for this branch). Track it to
 * completion with getJobStatus(jobId).
 */
export async function requestFullReport(jobId: string): Promise<void> {
  await jobsClient.post("/jobs", { fullReport: true, jobId });
}
